import { ReactElement, useEffect } from 'react';
import { Control, Controller, RegisterOptions, useFormContext } from 'react-hook-form';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker, { registerLocale } from 'react-datepicker';
import en from 'date-fns/locale/en-US';
import clsx from 'clsx';

registerLocale('en', en);

type Props = {
  id: string;
  label: string;
  disabled?: boolean;
  noMarginTop?: boolean;
  validationObject?: RegisterOptions;
  control?: Control<any>;
  defaultValue?: Date | null; // Allow null to handle initial empty values
  minDate?: Date | null;
};

/**
 * A dropdown react component.
 * @param props - DropdownProps.
 * @returns - React element to capture user selection.
 */
export default function CustomDatePicker(props: Props): ReactElement {
  const { id, label, disabled, noMarginTop, validationObject, control, defaultValue, minDate } = props;
  const { setValue, watch } = useFormContext();

  // Watch the current field value
  const fieldValue = watch(id);

  useEffect(() => {
    if (defaultValue !== undefined && defaultValue !== fieldValue) {
      setValue(id, defaultValue);
    }
  }, [defaultValue, id, fieldValue, setValue]);

  return (
    <div className={`w-full ${!noMarginTop && 'mt-4'}`}>
      {label && (
        <div className="text-gray-500 dark:text-gray-500 text-sm">
          {label}
          {validationObject?.required ? ' *' : ''}
        </div>
      )}
      <Controller
        name={id}
        rules={validationObject}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <>
            <DatePicker
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...field}
              disabled={disabled}
              onChange={(date) => field.onChange(date)}
              minDate={minDate ?? undefined}
              maxDate={new Date()}
              selected={field.value || null}
              locale="en"
              className={clsx(
                'w-full block py-1.5 px-3.5 text-base font-normal dark:text-gray-300 mt-4 placeholder-gray-400 border rounded dark:bg-container-500 focus:dark:border-primary-300 focus-within:dark:ring-transparent',
                { 'border-error-300': error },
                { 'border-gray-700': !error }
              )}
              placeholderText={label}
              showTimeSelect
              dateFormat="dd/MM/yyyy, hh:mm"
            />
            <p className="text-error-500 mt-1">{error?.message}</p>
          </>
        )}
      />
    </div>
  );
}
